const WIDTH = 1400
const PADDING = 48
const LABEL_WIDTH = 132
const CARD_WIDTH = 100
const CARD_HEIGHT = 140
const CARD_GAP = 10
const ROW_PADDING = 14
const MAX_CANVAS_HEIGHT = 16000
const FONT = '"Microsoft YaHei", "PingFang SC", sans-serif'
const imageCache = new Map()

function fitText(ctx, text, maxWidth) {
  const chars = Array.from(String(text || ''))
  if (ctx.measureText(chars.join('')).width <= maxWidth) return chars.join('')
  while (chars.length && ctx.measureText(`${chars.join('')}…`).width > maxWidth) chars.pop()
  return `${chars.join('')}…`
}

function roundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
}

function loadCover(url) {
  if (imageCache.has(url)) return Promise.resolve(imageCache.get(url))
  return new Promise(resolve => {
    if (!url) { resolve(null); return }
    const img = new Image()
    let done = false
    const finish = value => {
      if (done) return
      done = true
      clearTimeout(timer)
      img.onload = null
      img.onerror = null
      if (value && !url.startsWith('blob:')) {
        imageCache.set(url, value)
        if (imageCache.size > 120) imageCache.delete(imageCache.keys().next().value)
      }
      resolve(value)
    }
    const timer = setTimeout(() => finish(null), 12000)
    img.crossOrigin = 'anonymous'
    img.onload = () => finish(img)
    img.onerror = () => finish(null)
    img.src = url
  })
}

async function loadCovers(games) {
  const covers = new Map()
  let missing = 0
  for (let index = 0; index < games.length; index += 8) {
    const batch = games.slice(index, index + 8)
    const loaded = await Promise.all(batch.map(game => loadCover(game.cover)))
    loaded.forEach((image, offset) => {
      covers.set(batch[offset].id, image)
      if (!image) missing++
    })
  }
  return { covers, missing }
}

async function loadBackground(background) {
  if (!background || !background.url || !['current', 'upload'].includes(background.type)) return null
  const id = String(background.id || '')
  if (background.type === 'current' && /^[0-9]+$/.test(id)) {
    const proxied = await loadCover(`/api/open/background/file/${id}`)
    if (proxied) return proxied
  }
  return loadCover(background.url)
}

function drawPattern(ctx, name, height, color, backgroundColor) {
  const tile = document.createElement('canvas')
  tile.width = 80
  tile.height = 80
  const pen = tile.getContext('2d')
  const validColor = value => /^#[0-9a-fA-F]{6}$/.test(value)
  pen.fillStyle = validColor(backgroundColor) ? backgroundColor : '#f3f7fc'
  pen.fillRect(0, 0, 80, 80)
  pen.strokeStyle = validColor(color) ? color : '#77a9db'
  pen.fillStyle = pen.strokeStyle
  pen.globalAlpha = 0.3
  if (name === 'grid') {
    pen.strokeRect(0, 0, 80, 80)
    pen.beginPath()
    pen.moveTo(40, 0); pen.lineTo(40, 80)
    pen.moveTo(0, 40); pen.lineTo(80, 40)
    pen.stroke()
  } else if (name === 'diagonal') {
    for (let offset = -80; offset <= 160; offset += 24) {
      pen.beginPath()
      pen.moveTo(offset, 0)
      pen.lineTo(offset + 80, 80)
      pen.stroke()
    }
  } else if (name === 'checker') {
    pen.fillRect(0, 0, 40, 40)
    pen.fillRect(40, 40, 40, 40)
  } else if (name === 'waves') {
    for (const y of [0, 20, 40, 60, 80]) {
      pen.beginPath()
      pen.moveTo(0, y)
      pen.quadraticCurveTo(20, y + 16, 40, y)
      pen.quadraticCurveTo(60, y - 16, 80, y)
      pen.stroke()
    }
  } else if (name === 'stars') {
    for (const [x, y] of [[20, 20], [60, 60]]) {
      pen.beginPath()
      pen.moveTo(x, y - 8); pen.lineTo(x + 3, y - 3); pen.lineTo(x + 8, y)
      pen.lineTo(x + 3, y + 3); pen.lineTo(x, y + 8); pen.lineTo(x - 3, y + 3)
      pen.lineTo(x - 8, y); pen.lineTo(x - 3, y - 3); pen.closePath(); pen.fill()
    }
  } else {
    for (const x of [20, 60]) for (const y of [20, 60]) {
      pen.beginPath()
      pen.arc(x, y, 4, 0, Math.PI * 2)
      pen.fill()
    }
  }
  ctx.fillStyle = ctx.createPattern(tile, 'repeat')
  ctx.fillRect(0, 0, WIDTH, height)
}

function drawBackground(ctx, image, height, background) {
  if (background && background.type === 'color') {
    ctx.fillStyle = /^#[0-9a-fA-F]{6}$/.test(background.color) ? background.color : '#f4f7fb'
    ctx.fillRect(0, 0, WIDTH, height)
    return
  }
  if (background && background.type === 'pattern') {
    drawPattern(ctx, background.pattern, height, background.color, background.backgroundColor)
    return
  }
  const gradient = ctx.createLinearGradient(0, 0, WIDTH, height)
  gradient.addColorStop(0, '#eaf4ff')
  gradient.addColorStop(1, '#fff1f5')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, WIDTH, height)
  if (!image) return

  // Keep the artwork at a readable scale on tall boards instead of cropping it to a thin strip.
  const segmentHeight = Math.min(height, 1600)
  const scale = Math.max(WIDTH / image.naturalWidth, segmentHeight / image.naturalHeight)
  const imageWidth = image.naturalWidth * scale
  const imageHeight = image.naturalHeight * scale
  for (let y = 0; y < height; y += segmentHeight) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(0, y, WIDTH, Math.min(segmentHeight, height - y))
    ctx.clip()
    ctx.drawImage(image, (WIDTH - imageWidth) / 2, y + (segmentHeight - imageHeight) / 2, imageWidth, imageHeight)
    ctx.restore()
  }
  ctx.fillStyle = 'rgba(255, 255, 255, .58)'
  ctx.fillRect(0, 0, WIDTH, height)
}

function drawCover(ctx, image, x, y, width, height) {
  if (!image) {
    ctx.fillStyle = '#dce6f1'
    ctx.fillRect(x, y, width, height)
    ctx.fillStyle = '#8395a9'
    ctx.font = `600 14px ${FONT}`
    ctx.textAlign = 'center'
    ctx.fillText('暂无封面', x + width / 2, y + height / 2)
    ctx.textAlign = 'left'
    return
  }
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
  const drawWidth = image.naturalWidth * scale
  const drawHeight = image.naturalHeight * scale
  ctx.drawImage(image, x - (drawWidth - width) / 2, y - (drawHeight - height) / 2, drawWidth, drawHeight)
}

function drawGame(ctx, game, image, x, y) {
  ctx.save()
  roundedRect(ctx, x, y, CARD_WIDTH, CARD_HEIGHT, 7)
  ctx.clip()
  drawCover(ctx, image, x, y, CARD_WIDTH, CARD_HEIGHT)
  const shade = ctx.createLinearGradient(0, y + CARD_HEIGHT - 58, 0, y + CARD_HEIGHT)
  shade.addColorStop(0, 'rgba(10, 20, 34, 0)')
  shade.addColorStop(1, 'rgba(10, 20, 34, .9)')
  ctx.fillStyle = shade
  ctx.fillRect(x, y + CARD_HEIGHT - 58, CARD_WIDTH, 58)
  ctx.fillStyle = '#fff'
  ctx.font = `600 11px ${FONT}`
  const name = game.translatedName || game.name || game.gameId
  ctx.fillText(fitText(ctx, name, CARD_WIDTH - 10), x + 5, y + CARD_HEIGHT - 24)
  ctx.font = `10px ${FONT}`
  ctx.fillText(fitText(ctx, game.gameId || game.id, CARD_WIDTH - 10), x + 5, y + CARD_HEIGHT - 8)
  ctx.restore()
}

export function downloadTierBoardImage(blob, title) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const safeTitle = (title || '游戏分级板').replace(/[\\/:*?"<>|]/g, '_').slice(0, 60) || '游戏分级板'
  link.download = `${safeTitle}.png`
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

export async function renderTierBoardImage(board, background) {
  const snapshot = board.tiers.map(tier => ({
    label: tier.label || tier.id,
    color: tier.color,
    games: (board.placements[tier.id] || []).map(id => ({ ...board.items[id] }))
  }))
  const games = snapshot.flatMap(tier => tier.games)
  if (!games.length) throw new Error('请先将至少 1 部作品放入分级区')

  const innerWidth = WIDTH - PADDING * 2
  const cardStart = PADDING + LABEL_WIDTH + ROW_PADDING
  const columns = Math.floor((WIDTH - PADDING - ROW_PADDING - cardStart + CARD_GAP) / (CARD_WIDTH + CARD_GAP))
  const rowHeights = snapshot.map(tier => Math.max(CARD_HEIGHT + ROW_PADDING * 2,
    Math.ceil(tier.games.length / columns) * (CARD_HEIGHT + CARD_GAP) - CARD_GAP + ROW_PADDING * 2))
  const height = 174 + rowHeights.reduce((sum, value) => sum + value + 8, 0) + 40
  if (height > MAX_CANVAS_HEIGHT) throw new Error('作品过多，导出图片超过浏览器尺寸限制；请拆分到多个分级 Tab')

  const [{ covers, missing }, backgroundImage] = await Promise.all([loadCovers(games), loadBackground(background)])
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('浏览器无法创建图片画布')
  drawBackground(ctx, backgroundImage, height, background)
  ctx.fillStyle = 'rgba(255, 255, 255, .82)'
  roundedRect(ctx, PADDING, 30, innerWidth, 112, 12)
  ctx.fill()
  ctx.fillStyle = '#409eff'
  ctx.fillRect(PADDING, 44, 6, 70)
  ctx.fillStyle = '#273746'
  ctx.font = `700 34px ${FONT}`
  ctx.fillText(fitText(ctx, board.title || '未命名分级', innerWidth - 24), PADDING + 22, 77)
  ctx.fillStyle = '#718096'
  ctx.font = `15px ${FONT}`
  ctx.fillText(`${games.length} 部作品 · 游戏分级板`, PADDING + 23, 106)

  let y = 174
  snapshot.forEach((tier, index) => {
    const rowHeight = rowHeights[index]
    ctx.fillStyle = 'rgba(255, 255, 255, .88)'
    roundedRect(ctx, PADDING, y, innerWidth, rowHeight, 10)
    ctx.fill()
    ctx.save()
    roundedRect(ctx, PADDING, y, LABEL_WIDTH, rowHeight, 10)
    ctx.clip()
    ctx.fillStyle = tier.color
    ctx.fillRect(PADDING, y, LABEL_WIDTH, rowHeight)
    ctx.restore()
    ctx.fillStyle = '#23303c'
    ctx.font = `700 24px ${FONT}`
    ctx.textAlign = 'center'
    ctx.fillText(fitText(ctx, tier.label, LABEL_WIDTH - 16), PADDING + LABEL_WIDTH / 2, y + rowHeight / 2 + 8)
    ctx.textAlign = 'left'
    tier.games.forEach((game, gameIndex) => {
      const x = cardStart + (gameIndex % columns) * (CARD_WIDTH + CARD_GAP)
      const cardY = y + ROW_PADDING + Math.floor(gameIndex / columns) * (CARD_HEIGHT + CARD_GAP)
      drawGame(ctx, game, covers.get(game.id), x, cardY)
    })
    y += rowHeight + 8
  })

  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('图片生成失败，请重试')
  return { blob, missingCovers: missing, missingBackground: Boolean(background && background.url && !backgroundImage) }
}
